/**
 * Prohibited Topics Admin Component
 * Lets admins tune the client-side search-query blocklist without a code deploy.
 * Uses whole-word regex matching (e.g. "ied" only matches the standalone word,
 * never inside "certified").
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { API, setProhibitedTopics as _updateInMemory } from '../../utils/api';

const ProhibitedTopicsAdmin = ({ showToast }) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState(null);

  const bgColor = isDarkMode ? 'rgba(253, 251, 247, 0.95)' : 'rgba(255, 255, 255, 0.98)';
  const cardBg = isDarkMode ? '#1e293b' : 'rgba(248, 250, 252, 0.9)';
  const textColor = isDarkMode ? '#e2e8f0' : '#1e293b';
  const mutedColor = isDarkMode ? '#a1a1aa' : '#64748b';
  const inputBg = isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)';

  const fetchTopics = useCallback(async () => {
    try {
      const r = await fetch(`${API}/prohibited-topics`);
      const data = await r.json();
      setTopics(data.topics || []);
    } catch (e) {
      showToast && showToast('Failed to load prohibited topics', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);

  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/prohibited-topics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ topics: next }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const data = await r.json();
      setTopics(data.topics);
      _updateInMemory(data.topics);
      showToast && showToast('Prohibited topics saved', 'success');
    } catch (e) {
      showToast && showToast(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }, [token, showToast]);

  const handleAdd = () => {
    const clean = newTopic.trim().toLowerCase();
    if (!clean) return;
    if (topics.includes(clean)) {
      showToast && showToast('Already in list', 'warning');
      return;
    }
    save([...topics, clean]);
    setNewTopic('');
  };

  const handleRemove = (t) => save(topics.filter(x => x !== t));

  const handleReset = async () => {
    if (!window.confirm('Reset prohibited-topics list to defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/prohibited-topics/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('Reset failed');
      const data = await r.json();
      setTopics(data.topics);
      _updateInMemory(data.topics);
      showToast && showToast('Reset to defaults', 'success');
    } catch (e) {
      showToast && showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Live tester — mirrors the same word-boundary logic used at search time.
  const runTest = () => {
    if (!testQuery.trim()) { setTestResult(null); return; }
    const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\b(${topics.map(escape).join('|')})\\b`, 'i');
    const match = re.exec(testQuery);
    setTestResult({ blocked: !!match, matchedTopic: match ? match[1] : null });
  };

  if (loading) {
    return <div style={{ padding: 20, color: textColor }}>Loading prohibited topics…</div>;
  }

  return (
    <div
      data-testid="prohibited-topics-admin"
      style={{ padding: 20, background: bgColor, color: textColor, borderRadius: 12 }}
    >
      <h2 style={{ marginTop: 0, fontSize: 22 }}>Prohibited Search Topics</h2>
      <p style={{ color: mutedColor, marginBottom: 20 }}>
        Search queries containing any of these words or phrases (matched as whole words)
        will be blocked before hitting the search API. Tune the list here without a code deploy.
      </p>

      {/* Add new */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          data-testid="prohibited-topic-input"
          value={newTopic}
          onChange={e => setNewTopic(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          placeholder="Add a word or phrase (e.g. 'sarin' or 'how to make a bomb')"
          style={{
            flex: 1, padding: '10px 12px', background: inputBg, color: textColor,
            border: `1px solid ${mutedColor}`, borderRadius: 8, fontSize: 14
          }}
        />
        <button
          data-testid="prohibited-topic-add-btn"
          onClick={handleAdd}
          disabled={saving || !newTopic.trim()}
          style={{
            padding: '10px 18px', background: '#FCD34D', color: '#0F172A',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600
          }}
        >Add</button>
        <button
          data-testid="prohibited-topic-reset-btn"
          onClick={handleReset}
          disabled={saving}
          style={{
            padding: '10px 14px', background: 'transparent', color: mutedColor,
            border: `1px solid ${mutedColor}`, borderRadius: 8, cursor: 'pointer'
          }}
        >Reset to defaults</button>
      </div>

      {/* Live tester */}
      <div style={{ background: cardBg, padding: 14, borderRadius: 10, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Test a query</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            data-testid="prohibited-topic-test-input"
            value={testQuery}
            onChange={e => setTestQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runTest()}
            placeholder="e.g. Construction carpentry certified information"
            style={{
              flex: 1, padding: '8px 12px', background: inputBg, color: textColor,
              border: `1px solid ${mutedColor}`, borderRadius: 6
            }}
          />
          <button
            data-testid="prohibited-topic-test-btn"
            onClick={runTest}
            style={{
              padding: '8px 16px', background: '#10b981', color: '#0F172A',
              border: 'none', borderRadius: 6, cursor: 'pointer'
            }}
          >Test</button>
        </div>
        {testResult !== null && (
          <div
            data-testid="prohibited-topic-test-result"
            style={{
              marginTop: 10, padding: 10, borderRadius: 6,
              background: testResult.blocked ? '#fee2e2' : '#d1fae5',
              color: testResult.blocked ? '#991b1b' : '#065f46'
            }}
          >
            {testResult.blocked
              ? `BLOCKED — matched term: "${testResult.matchedTopic}"`
              : 'ALLOWED — no prohibited term matched'}
          </div>
        )}
      </div>

      {/* List */}
      <div style={{ color: mutedColor, marginBottom: 8 }}>
        {topics.length} topic(s) in list
      </div>
      <div
        data-testid="prohibited-topics-list"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {topics.map(t => (
          <span
            key={t}
            data-testid={`prohibited-topic-chip-${t.replace(/\s+/g, '-')}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', background: cardBg,
              border: `1px solid ${mutedColor}`, borderRadius: 20, fontSize: 13
            }}
          >
            {t}
            <button
              onClick={() => handleRemove(t)}
              disabled={saving}
              title={`Remove "${t}"`}
              aria-label={`Remove ${t}`}
              style={{
                background: 'none', border: 'none', color: '#ef4444',
                cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1
              }}
            >×</button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default ProhibitedTopicsAdmin;
